import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Mail, Phone, MapPin, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { getApiEndpoint } from '../config/api';

const AdminBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // 'all', 'new', 'contacted', 'closed'

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const response = await axios.get(getApiEndpoint('get_bookings.php'));
            setBookings(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching bookings", error);
            setLoading(false);
        }
    };

    const updateStatus = async (id, newStatus) => {
        try {
            await axios.put(getApiEndpoint('get_bookings.php'), {
                id: id,
                status: newStatus
            });
            fetchBookings(); // Refresh list
        } catch (error) {
            console.error("Error updating status", error);
            alert("Failed to update status");
        }
    };

    const deleteBooking = async (id) => {
        if (window.confirm("Are you sure you want to delete this booking inquiry?")) {
            try {
                await axios.delete(getApiEndpoint(`get_bookings.php?id=${id}`));
                fetchBookings(); // Refresh list
            } catch (error) {
                console.error("Error deleting booking", error);
                alert("Failed to delete booking");
            }
        }
    };

    // Filter bookings
    const filteredBookings = bookings.filter(booking => {
        if (filter === 'all') return true;
        return booking.status === filter;
    });

    // Count by status
    const counts = {
        all: bookings.length,
        new: bookings.filter(b => b.status === 'new').length,
        contacted: bookings.filter(b => b.status === 'contacted').length,
        closed: bookings.filter(b => b.status === 'closed').length
    };

    const getStatusBadge = (status) => {
        const styles = {
            new: { bg: '#dbeafe', color: '#1e40af' },
            contacted: { bg: '#fef3c7', color: '#92400e' },
            closed: { bg: '#d1fae5', color: '#065f46' }
        };
        const style = styles[status] || styles.new;
        return (
            <span style={{
                backgroundColor: style.bg,
                color: style.color,
                padding: '0.25rem 0.75rem',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                fontWeight: '600',
                textTransform: 'uppercase'
            }}>
                {status}
            </span>
        );
    };

    if (loading) return <div className="p-8">Loading bookings...</div>;

    return (
        <div>
            <div className="admin-header">
                <h2>Booking Inquiries</h2>
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    {counts.all} total inquiries
                </div>
            </div>

            {/* Filter Tabs */}
            <div style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: '2rem',
                borderBottom: '1px solid #e2e8f0',
                paddingBottom: '1rem'
            }}>
                {['all', 'new', 'contacted', 'closed'].map(status => (
                    <button
                        key={status}
                        onClick={() => setFilter(status)}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            backgroundColor: filter === status ? '#f1f5f9' : 'transparent',
                            borderRadius: '0.375rem',
                            fontWeight: filter === status ? '600' : '400',
                            cursor: 'pointer',
                            textTransform: 'capitalize'
                        }}
                    >
                        {status} ({counts[status]})
                    </button>
                ))}
            </div>

            {/* Bookings List */}
            {filteredBookings.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '4rem',
                    color: '#64748b'
                }}>
                    <p>No booking inquiries found</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {filteredBookings.map((booking) => (
                        <div
                            key={booking.id}
                            style={{
                                backgroundColor: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.5rem',
                                padding: '1.5rem',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                        >
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto',
                                gap: '1.5rem',
                                alignItems: 'start'
                            }}>
                                {/* Left: Booking Details */}
                                <div>
                                    {/* Property Info */}
                                    <div style={{ marginBottom: '1rem' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            marginBottom: '0.5rem'
                                        }}>
                                            <MapPin size={16} color="#64748b" />
                                            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
                                                {booking.property_title || 'Unknown Property'}
                                            </h3>
                                            {booking.property_type && (
                                                <span style={{
                                                    backgroundColor: '#f1f5f9',
                                                    padding: '0.125rem 0.5rem',
                                                    borderRadius: '0.25rem',
                                                    fontSize: '0.75rem',
                                                    color: '#475569'
                                                }}>
                                                    {booking.property_type}
                                                </span>
                                            )}
                                        </div>
                                        {booking.property_location && (
                                            <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                                                {booking.property_location}
                                            </p>
                                        )}
                                    </div>

                                    {/* Customer Info */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                        gap: '1rem',
                                        marginBottom: '1rem'
                                    }}>
                                        <div>
                                            <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
                                                Customer Name
                                            </p>
                                            <p style={{ fontSize: '0.875rem', fontWeight: '500', margin: 0 }}>
                                                {booking.name}
                                            </p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <Mail size={16} color="#64748b" />
                                            <a
                                                href={`mailto:${booking.email}`}
                                                style={{ fontSize: '0.875rem', color: '#3b82f6', textDecoration: 'none' }}
                                            >
                                                {booking.email}
                                            </a>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <Phone size={16} color="#64748b" />
                                            <a
                                                href={`tel:${booking.phone}`}
                                                style={{ fontSize: '0.875rem', color: '#3b82f6', textDecoration: 'none' }}
                                            >
                                                {booking.phone}
                                            </a>
                                        </div>
                                    </div>

                                    {/* Date & Status */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        fontSize: '0.75rem',
                                        color: '#64748b'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                            <Calendar size={14} />
                                            {new Date(booking.created_at).toLocaleString()}
                                        </div>
                                        {getStatusBadge(booking.status)}
                                    </div>
                                </div>

                                {/* Right: Actions */}
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.5rem',
                                    minWidth: '150px'
                                }}>
                                    {booking.status === 'new' && (
                                        <button
                                            onClick={() => updateStatus(booking.id, 'contacted')}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                backgroundColor: '#3b82f6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            <CheckCircle size={16} />
                                            Mark Contacted
                                        </button>
                                    )}
                                    {booking.status === 'contacted' && (
                                        <button
                                            onClick={() => updateStatus(booking.id, 'closed')}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                backgroundColor: '#10b981',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            <CheckCircle size={16} />
                                            Mark Closed
                                        </button>
                                    )}
                                    {booking.status === 'closed' && (
                                        <button
                                            onClick={() => updateStatus(booking.id, 'new')}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                backgroundColor: '#64748b',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem',
                                                fontWeight: '500'
                                            }}
                                        >
                                            Reopen
                                        </button>
                                    )}
                                    <button
                                        onClick={() => deleteBooking(booking.id)}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            backgroundColor: 'transparent',
                                            color: '#ef4444',
                                            border: '1px solid #ef4444',
                                            borderRadius: '0.375rem',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '0.5rem'
                                        }}
                                    >
                                        <Trash2 size={16} />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminBookings;
